// ===========================================
// Authentication Context - Provider Component
// ===========================================

import { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { clearHistoryEntryState } from '../hooks/useHistoryEntryState';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from '../services/api';
import { onConsentRequired } from '../services/consentSignal';
import {
  AuthContext,
  AuthState,
  LoginCredentials,
  LoginResponse,
  TwoFactorRequiredError,
  User,
} from './AuthContext.types';

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        setState({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }

      try {
        const response = await apiClient.get<{ data: { user: User } }>('/auth/me');
        setState({
          user: response.data.user,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch (error) {
        // #524: clear storage only when the server REFUSED the session (a 401: the interceptor
        // has already tried to refresh). A network error or a 5xx proves nothing, so keep the
        // tokens and the next load, back online, restores the session.
        if (isAxiosError(error) && error.response?.status === 401) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
        setState({ user: null, isAuthenticated: false, isLoading: false });
      }
    };

    initAuth();
  }, []);

  // Complete login after receiving tokens (used by both normal login and 2FA verify)
  const completeLogin = useCallback(async (data: { accessToken: string; refreshToken: string; user: User }): Promise<User> => {
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('refreshToken', data.refreshToken);

    setState({
      user: data.user,
      isAuthenticated: true,
      isLoading: false,
    });

    // Invalidate all queries to ensure fresh data is fetched after login
    // This ensures badges (unread messages, pending meet & greets) appear immediately
    await queryClient.invalidateQueries();

    return data.user;
  }, [queryClient]);

  // Login function - returns user for redirect logic
  // Throws TwoFactorRequiredError if 2FA verification is needed.
  // A parent who still has to agree to the T&Cs logs in normally: the user carries
  // `requiresConsentAcknowledgement` and ProtectedRoute shows the consent gate (#442). The
  // dialog no longer lives on the login page, so a refresh can't skip it.
  const login = useCallback(async (credentials: LoginCredentials): Promise<User> => {
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials);

    // Check if 2FA verification is required
    if (response.data.requires2FA && response.data.tempToken) {
      throw new TwoFactorRequiredError(response.data.tempToken);
    }

    return completeLogin(response.data as { accessToken: string; refreshToken: string; user: User });
  }, [completeLogin]);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await apiClient.post('/auth/logout', { refreshToken });
      }
    } catch {
      // Ignore errors during logout
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      // Purge SW api-cache so the next user on this browser cannot see this
      // user's cached /auth/me, /admin/* responses, etc. Bug observed 2026-04-30
      // where a parent loaded admin layout served from a previous admin session.
      if ('caches' in window) {
        caches.delete('api-cache').catch(() => {});
      }
      // Drop any in-memory React Query data tied to the previous identity.
      queryClient.clear();
      // …and the per-tab lists kept for the Back button (the invoices prompt, final /qa L2).
      clearHistoryEntryState();
      setState({ user: null, isAuthenticated: false, isLoading: false });
    }
  }, [queryClient]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const response = await apiClient.get<{ data: { user: User } }>('/auth/me');
      setState((prev) => ({
        ...prev,
        user: response.data.user,
      }));
    } catch (error) {
      // #524: log out only when the server REFUSED the session (a 401: the interceptor has
      // already tried to refresh). A network blip on "Check again" must not log a parent out.
      if (isAxiosError(error) && error.response?.status === 401) {
        await logout();
      }
    }
  }, [logout]);

  // #442: an already-open session hit the server's consent gate (403 CONSENT_REQUIRED, see
  // services/api.ts). Re-read /auth/me so ProtectedRoute swaps the app for the consent gate.
  // Only while signed in (a late signal after logout must not bounce anyone), at most one
  // request in flight and one per 5 s, and a failure changes nothing: a network blip must not
  // log the parent out (plan /qa A11).
  const consentRecheck = useRef<{ inFlight: boolean; last: number }>({ inFlight: false, last: 0 });
  const isAuthenticated = state.isAuthenticated;
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return onConsentRequired(() => {
      const r = consentRecheck.current;
      const now = Date.now();
      if (r.inFlight || now - r.last < 5000) return;
      r.inFlight = true;
      r.last = now;
      apiClient
        .get<{ data: { user: User } }>('/auth/me')
        .then((response) => {
          setState((prev) => (prev.isAuthenticated ? { ...prev, user: response.data.user } : prev));
        })
        .catch(() => undefined)
        .finally(() => {
          r.inFlight = false;
        });
    });
  }, [isAuthenticated]);

  return (
    <AuthContext.Provider value={{ ...state, login, completeLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}
